import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { FadeUp, MaskLine } from "@/components/Reveal";
import { Mark } from "@/components/Logo";
import { useLang } from "@/context/LangContext";
import { useStore } from "@/context/StoreContext";
import { imgUrl, waLink } from "@/lib/api";

function ProductCard({ p, i, onOpen }) {
  const { t, tr, fmt } = useLang();
  const out = (p.stock ?? 0) <= 0;
  return (
    <motion.button
      data-testid={`catalog-product-card-${p.id}`}
      onClick={() => onOpen(p)}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.12 }}
      className="group block w-full text-start"
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-[#141011] arch-sm">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(198,167,106,0.22),transparent_65%)] transition-opacity duration-700 group-hover:opacity-100" />
        {p.images?.[0] ? (
          <img src={imgUrl(p.images[0])} alt={tr(p.name)} loading="lazy" style={{ transitionDuration: "1.4s", transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }} className="absolute inset-0 h-full w-full object-cover transition-transform group-hover:scale-[1.06]" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Mark className="h-24 w-20 text-[#C6A76A]/40" />
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0B0B0C]/80 to-transparent" />
        {out && <span className="absolute start-4 top-6 bg-[#0B0B0C]/85 px-3 py-1 text-[0.65rem] tracking-[0.25em] text-[#F7F3EB]">{t("sold_out")}</span>}
        <span className="absolute bottom-5 start-5 translate-y-3 text-[0.7rem] tracking-[0.25em] text-[#F7F3EB] opacity-0 transition-all duration-700 group-hover:translate-y-0 group-hover:opacity-100 rtl:tracking-normal rtl:text-sm">
          {t("discover")} →
        </span>
      </div>
      <div className="mt-6 flex items-start justify-between gap-4">
        <div>
          {tr(p.family) && <p className="eyebrow mb-2 !text-[0.62rem]">{tr(p.family)}</p>}
          <h3 className="font-display text-2xl text-[#F7F3EB] sm:text-[1.7rem]">{tr(p.name)}</h3>
          {p.volume && <p className="mt-1 font-latin text-xs tracking-[0.2em] text-[#F7F3EB]/60">{p.volume}</p>}
        </div>
        <div className="shrink-0 text-end">
          <p data-testid={`product-price-${p.id}`} className="font-latin text-base text-[#F7F3EB] rtl:font-sans">{fmt(p.price)}</p>
          {p.compare_price > p.price && <p className="font-latin text-xs text-[#F7F3EB]/50 line-through">{fmt(p.compare_price)}</p>}
        </div>
      </div>
      <div className="mt-5 h-px w-full origin-[left] scale-x-[0.15] bg-[#C6A76A] transition-transform duration-700 group-hover:scale-x-100 rtl:origin-[right]" />
    </motion.button>
  );
}

function EmptyState() {
  const { t } = useLang();
  const { settings } = useStore();
  const wa = settings?.whatsapp_number;
  return (
    <FadeUp className="relative mx-auto flex max-w-xl flex-col items-center text-center" data-testid="catalog-empty-state">
      <div className="relative flex h-72 w-52 items-center justify-center sm:h-80 sm:w-60" data-testid="catalog-empty-state">
        <svg viewBox="0 0 200 300" className="absolute inset-0 h-full w-full text-[#C6A76A]" fill="none">
          <motion.path d="M10 295V100a90 90 0 0 1 180 0v195" stroke="currentColor" strokeWidth="0.8" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 2.4, ease: [0.65, 0, 0.35, 1] }} />
          <motion.path d="M24 295V104a76 76 0 0 1 152 0v191" stroke="currentColor" strokeOpacity="0.35" strokeWidth="0.6" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 2.4, delay: 0.3, ease: [0.65, 0, 0.35, 1] }} />
        </svg>
        <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}>
          <Mark className="h-20 w-16 text-[#C6A76A]" />
        </motion.div>
      </div>
      <h3 className="font-display mt-12 text-3xl text-[#F7F3EB] sm:text-4xl">{t("empty_title")}</h3>
      <p className="mt-4 max-w-sm text-base leading-relaxed text-[#F7F3EB]/70">{t("empty_text")}</p>
      {wa && (
        <a data-testid="empty-state-whatsapp-btn" href={waLink(settings.whatsapp_number, t("wa_contact_msg"))} target="_blank" rel="noreferrer" className="btn-gold mt-10 inline-flex items-center gap-3 border border-[#C6A76A] px-7 py-3.5 text-[0.75rem] tracking-[0.22em] text-[#F7F3EB] transition-colors duration-500 hover:text-[#0B0B0C] rtl:text-sm rtl:tracking-normal">
          <MessageCircle className="h-4 w-4" strokeWidth={1.3} /> {t("write_whatsapp")}
        </a>
      )}
    </FadeUp>
  );
}

export function Collection({ onOpen }) {
  const { t, rtl } = useLang();
  const { products, loaded } = useStore();
  return (
    <section id="collection" data-testid="collection-section" className="grain relative bg-[#0B0B0C] text-[#F7F3EB]">
      <div className="mx-auto max-w-[1440px] px-5 py-28 sm:px-10 lg:py-40">
        <div className="mb-20 flex flex-col justify-between gap-8 border-b border-[#C6A76A]/15 pb-10 md:flex-row md:items-end">
          <div>
            <FadeUp className="flex items-center gap-4">
              <span className="font-latin text-xs tracking-[0.3em] text-[#F7F3EB]/60">02</span>
              <span className="h-px w-10 bg-[#C6A76A]" />
              <span className="eyebrow">{t("collection_over")}</span>
            </FadeUp>
            <h2 className={`font-display mt-8 ${rtl ? "text-5xl leading-[1.5] lg:text-7xl" : "text-5xl leading-none lg:text-[5.5rem]"}`}>
              <MaskLine inView>{t("collection_title")}</MaskLine>
            </h2>
          </div>
          {products.length > 0 && (
            <p data-testid="catalog-count" className="font-latin text-sm tracking-[0.2em] text-[#F7F3EB]/60 rtl:tracking-normal">
              {String(products.length).padStart(2, "0")} — {t("pieces")}
            </p>
          )}
        </div>
        {!loaded ? (
          <div className="h-80" />
        ) : products.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 gap-x-8 gap-y-20 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-grid">
            {products.map((p, i) => (
              <div key={p.id} className={i % 3 === 1 ? "lg:translate-y-16" : ""}>
                <ProductCard p={p} i={i} onOpen={onOpen} />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
